import {Injectable} from '@angular/core';
import {Decimal} from 'decimal.js';
import {Observable, of, Subject} from 'rxjs';
import {debounceTime, delay, map, switchMap, tap} from 'rxjs/operators';
import {SecurityService} from '../../../components/security';
import {SecurityAutocompleteService} from '../../../components/security-autocomplete';
import {getSymbolDecimalPlaces} from '../../../components/symbol-decimals';
import {
    ResolutionString,
    SearchSymbolResultItem,
    SymbolExt
} from '../../../components/trading-view-chart/charting-library';
import {
    LibrarySymbolInfo,
    SearchSymbolsCallback
} from '../../../components/trading-view-chart/charting-library/datafeed-api';
import {Exchange, EXCHANGE_NAMING} from '../../../enums/exchange';
import {QueryLangFilterOperator} from '../../../enums/query-lang-filter-operator';
import {QueryLangFilterCriterion} from '../../../models/filters/query-lang-filter-criterion';
import {Security} from '../../../models/security';
import {formatSecurityKey, parseSecurityKey, SecurityKey} from '../../../models/security-key';
import {CHART_RESOLUTIONS} from './chart-resolution';

interface NextSearchEvent {
    userInput: string;
    exchange: Exchange;
    symbolType: string;
    onResult: SearchSymbolsCallback;
}

const SEARCH_TYPING_DEBOUNCE_TIME = 400;

type CommonSymbolInfoParams = Pick<
    LibrarySymbolInfo,
    'timezone'
    | 'format'
    | 'session'
    | 'has_intraday'
    | 'has_daily'
    | 'has_weekly_and_monthly'
    | 'has_no_volume'
    | 'type'
    | 'supported_resolutions'
    | 'minmov'
    | 'fractional'
    >;

const COMMON_SYMBOL_INFO_PARAMS: CommonSymbolInfoParams = {
    timezone:               'America/New_York',
    format:                 'price',
    session:                '24x7',
    has_intraday:           true,
    has_daily:              true,
    has_weekly_and_monthly: true,
    has_no_volume:          false,
    type:                   null,
    supported_resolutions:  CHART_RESOLUTIONS as ResolutionString[],
    minmov:                 1,
    fractional:             false
};

@Injectable()
export class ChartSecurityService {

    private readonly nextSearchEvent$ = new Subject<NextSearchEvent>();

    private securityKeyBySymbolExtDict: {[symbolExtStr: string]: SecurityKey} = {};

    constructor(
        private securityService: SecurityService,
        private securityAutocompleteService: SecurityAutocompleteService
    ) {
        this.listenToSearchEvents();
    }

    public searchSymbols(event: NextSearchEvent): void {
        this.nextSearchEvent$.next(event);
    }

    public resolveSymbol(ticker: string): Observable<LibrarySymbolInfo> {
        const securityKey = parseSecurityKey(ticker);
        const exchangeDisplayName = securityKey.securityExchange
            && EXCHANGE_NAMING[securityKey.securityExchange] || securityKey.securityExchange;

        if (securityKey.securityExchange) {
            const symbolExtStr = [
                securityKey.symbol,
                exchangeDisplayName
            ].join(':');

            // Cache it in order to be able to convert SymbolExt to SecurityKey before widget's settings storing.
            this.securityKeyBySymbolExtDict[symbolExtStr] = securityKey;
        }

        return securityKey.securityExchange
            ? this.securityService.getSecurityBySecurityKey(securityKey).pipe(
                map<Security, LibrarySymbolInfo>(security => security && ({
                    ...COMMON_SYMBOL_INFO_PARAMS,

                    name:            securityKey.symbol,

                    /**
                     * full_name must be equal to ticker,
                     * otherwise it causes another call of resolveSymbol for full_name.
                     */
                    full_name:       ticker,

                    base_name:       [ticker],
                    description:     security.longDescription || '',
                    exchange:        exchangeDisplayName,
                    listed_exchange: exchangeDisplayName,
                    ticker,
                    pricescale:      new Decimal(10).pow(getSymbolDecimalPlaces(security).price).toNumber()
                }))
            )
            : of(null).pipe(
                delay(0)
            );
    }

    public getSecurityKeyBySymbolExt(symbolExt: SymbolExt): SecurityKey {
        const symbolExtStr = [
            symbolExt.symbol,
            symbolExt.exchange
        ].join(':');

        return this.securityKeyBySymbolExtDict[symbolExtStr];
    }

    private listenToSearchEvents(): void {
        this.nextSearchEvent$.pipe(
            debounceTime(SEARCH_TYPING_DEBOUNCE_TIME),
            switchMap(event => this.autocomplete(event.userInput, event.exchange).pipe(
                map(securities => securities.map(security => this.createSearchSymbolResultItem(security))),
                tap(securities => event.onResult(securities))
            ))
        ).subscribe();
    }

    private autocompleteForExchange(userInput: string, exchange: Exchange): Observable<Security[]> {
        const filterByExchange = new QueryLangFilterCriterion({
            fieldNames: ['securityExchange'],
            operator: QueryLangFilterOperator.EQ,
            query: exchange
        });

        return this.securityService.autocomplete(userInput, {
            filters: [filterByExchange],
            provider: this.securityService
        });
    }

    private autocomplete(userInput: string, exchange?: Exchange): Observable<Security[]> {
        return exchange
            ? this.autocompleteForExchange(userInput, exchange)
            : this.securityAutocompleteService.autocomplete(userInput).pipe(
                map(autocompleteItems => autocompleteItems.map(item => item.security))
            );
    }

    private createSearchSymbolResultItem(security: Security): SearchSymbolResultItem {
        return {
            symbol:      security.symbol,
            full_name:   security.symbol,
            description: security.longDescription || '',
            exchange:    EXCHANGE_NAMING[security.securityExchange] || security.securityExchange,
            ticker:      formatSecurityKey(security),
            type:        security.currency
        };
    }

}
